import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { AuthenticationService } from 'app/auth/service';
import { User } from 'app/auth/models';
import { PermissionService } from 'app/auth/service/permission.service';

@Injectable({
  providedIn: 'root'
})
export class CoreMenuService {
  currentUser: User;
  onItemCollapsed: Subject<any>;
  onItemCollapseToggled: Subject<any>;

  // Private
  private _onMenuRegistered: BehaviorSubject<any>;
  private _onMenuUnregistered: BehaviorSubject<any>;
  private _onMenuChanged: BehaviorSubject<any>;
  private _currentMenuKey: string;
  private _registry: { [key: string]: any } = {};

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   */
  constructor(
    private _router: Router, 
    private _authenticationService: AuthenticationService,
    private _permissionService: PermissionService
  ) {
    // Subscribe to user changes and update menu when user changes
    this._authenticationService.currentUser.subscribe(x => {
      this.currentUser = x;
      // Refresh current menu when user changes
      if (this._currentMenuKey) {
        this.setCurrentMenu(this._currentMenuKey);
      }
    });

    // Set defaults
    this.onItemCollapsed = new Subject();
    this.onItemCollapseToggled = new Subject();

    // Set private defaults
    this._currentMenuKey = null;
    this._onMenuRegistered = new BehaviorSubject(null);
    this._onMenuUnregistered = new BehaviorSubject(null);
    this._onMenuChanged = new BehaviorSubject(null);
  }

  // Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * onMenuRegistered
   *
   * @returns {Observable<any>}
   */
  get onMenuRegistered(): Observable<any> {
    return this._onMenuRegistered.asObservable();
  }

  /**
   * onMenuUnregistered
   *
   * @returns {Observable<any>}
   */
  get onMenuUnregistered(): Observable<any> {
    return this._onMenuUnregistered.asObservable();
  }

  /**
   * onMenuChanged
   *
   * @returns {Observable<any>}
   */
  get onMenuChanged(): Observable<any> {
    return this._onMenuChanged.asObservable();
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Register the provided menu with the provided key
   *
   * @param key
   * @param menu
   */
  register(key, menu): void {
    // Confirm if the key already used
    if (this._registry[key]) {
      console.error(`Menu with the key '${key}' already exists. Either unregister it first or use a unique key.`);
      return;
    }


    // Filter menu items based on permissions before registering
    const filteredMenu = this.filterMenuByPermissions(menu);
    
    // Add filtered menu to registry
    this._registry[key] = menu;

    // Notify subject
    this._onMenuRegistered.next([key, menu]);
  }

  /**
   * Unregister the menu from the registry
   *
   * @param key
   */
  unregister(key): void {
    // Confirm if the menu exists
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);
    }

    // Unregister sidebar
    delete this._registry[key];

    // Notify subject
    this._onMenuUnregistered.next(key);
  }

  /**
   * Get menu from registry by key
   *
   * @param key
   * @returns {any}
   */
  getMenu(key): any {
    // Confirm if the menu exists
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);
      return;
    }

    // Get the menu
    const menu = this._registry[key];

    // Filter menu items based on permissions
    return this.filterMenuByPermissions(menu);
  }

  /**
   * Filter menu items based on user permissions
   * @param menu Menu items to filter
   * @returns Filtered menu items
   */
  private filterMenuByPermissions(menuItems: any[]): any[] {
    if (!menuItems) return [];

    console.log(this.currentUser?.role)
    // If user is admin, return all menu items without filtering
    if ((this.currentUser?.role)?.toLowerCase() == 'admin') {
      return menuItems;
    }

    return menuItems.filter(item => {
      // Always show sections
      if (item.type === 'section') {
        if (item.children) {
          const filteredChildren = this.filterMenuByPermissions(item.children);
          item.children = filteredChildren;
          // Show section only if it has visible children
          return filteredChildren.length > 0;
        }
        return true;
      }

      // Check role-based access
      if (item.role && Array.isArray(item.role)) {
        if (!this.currentUser || !item.role.includes(this.currentUser.role)) {
          return false;
        }
      }

      // Check permission-based access
      if (item.permission) {
        const { permission, module } = item.permission;
        if (!this._permissionService.hasPermission(permission, module)) {
          return false;
        }
      }

      // Handle children for collapsible menus
      if (item.type === 'collapsible' && item.children) {
        const filteredChildren = this.filterMenuByPermissions(item.children);
        item.children = filteredChildren;
        // Hide collapsible menu if all children are filtered out
        return filteredChildren.length > 0;
      }

      return true;
    });
  }

  /**
   * Get current menu
   *
   * @returns {any}
   */
  getCurrentMenu(): any {
    if (!this._currentMenuKey) {
      console.warn(`The current menu is not set.`);

      return;
    }

    return this.getMenu(this._currentMenuKey);
  }

  /**
   * Set menu with the key as the current menu
   *
   * @param key
   */
  setCurrentMenu(key): void {
    // Confirm if the sidebar exists
    if (!this._registry[key]) {
      console.warn(`Menu with the key '${key}' doesn't exist in the registry.`);

      return;
    }

    // Set current menu key
    this._currentMenuKey = key;

    // Notify subject
    this._onMenuChanged.next(key);
  }
}
